import React, { useEffect, useMemo, useRef } from "react";
import { CanvasRenderer, RenderedCanvas } from "@rosette/web_components/src/generic/render/RenderedCanvas.js";
import type { BoardController } from "@rosette/ts/src/business/game/controller/board/BoardController.js";
import type { BoardDirective } from "@rosette/ts/src/business/game/controller/board/BoardDirective.js";
import { useBoardController, useBoardDirective } from "@rosette/web_components/src/game/board/BoardUIContext.js";
import type { BoardAsset } from "@/components/game/board/BoardAsset.js";
import { drawPiece, rgb } from "@/components/game/render/render.js";
import type { MouseListener, MouseState } from "@rosette/web_components/src/generic/MouseListener.js";
import { useMouseListener } from "@rosette/web_components/src/generic/MouseListener.js";
import { Tile } from "@rosette/ts/src/royalur/model/Tile.js";
import { Piece } from "@rosette/ts/src/royalur/model/Piece.js";
import { Move } from "@rosette/ts/src/royalur/model/Move.js";
import type { Seconds } from "@rosette/ts/src/util/units.js";
import { getTimeSeconds, LONG_TIME_AGO } from "@rosette/ts/src/util/utils.js";
import { Vec2 } from "@rosette/ts/src/util/Vec2.js";
import type { PathPair } from "@rosette/ts/src/royalur/model/path/PathPair.js";
import {
    clamp,
    easeInOutCubic,
    easeInOutSine,
    min,
} from "@rosette/ts/src/util/numbers.js";
import { PlayerType } from "@rosette/ts/src/royalur/model/PlayerType.js";
import type { BoardShape } from "@rosette/ts/src/royalur/model/shape/BoardShape.js";
import { Fade } from "@rosette/ts/src/business/fades.js";
import { LRUCache } from "@rosette/ts/src/util/cache/LRUCache.js";
import { Key, registerKeyListener } from "@rosette/web_components/src/generic/KeyListener.js";
import { useGameTheme } from "@/components/game/theme/GameThemeContext.js";
import type { GameTheme } from "@/components/game/theme/GameTheme.js";
import Image from "next/image";
import type { APIGamePreferences } from "@rosette/ts/src/business/api/api_schema.js";
import { useGamePreferences } from "@/app/(api)/api/getGamePreferences/useGamePreferences.js";
import { useOptionalUser } from "@/components/env/EnvContext.js";
import { StandardBoardShape } from "@rosette/ts/src/royalur/model/shape/StandardBoardShape.js";
import type { GamePlayers } from "@rosette/ts/src/business/api/game/GamePlayers.js";
import { useRune } from "@rosette/react_utils/src/useRune.js";
import { useGameController } from "@/components/game/GameUIContext.js";
import type { AssetLoader } from "@/components/assets/AssetLoader.js";
import { useAssetLoader } from "@/components/assets/AssetContext.js";
import type { SharedRendererState } from "@rosette/web_components/src/game/SharedRendererState.js";
import { cn } from "@rosette/ts/src/util/cn.js";
import { NavigationGameEvent, NavigationType } from "@rosette/ts/src/business/game/event/NavigationGameEvent.js";
import { MoveBoardDirective } from "@rosette/ts/src/business/game/controller/board/MoveBoardDirective.js";
import { PlayBoardDirective } from "@rosette/ts/src/business/game/controller/board/PlayBoardDirective.js";
import { ReplayMovesBoardDirective } from "@rosette/ts/src/business/game/controller/board/ReplayMovesBoardDirective.js";
import { AnalysisBoardDirective } from "@rosette/ts/src/business/game/controller/board/AnalysisBoardDirective.js";
import { MoveCategoryIcon } from "@/components/icon/analysis/MoveCategoryIcon.js";
import { FadingDiv } from "@/components/layout/FadingDiv.js";
import { useBounds } from "@rosette/web_components/src/accessibility/useBounds.js";
import type { Rectangle } from "@rosette/ts/src/util/Rectangle.js";
import type { PlayerState } from "@rosette/ts/src/royalur/model/PlayerState.js";
import type { Board } from "@rosette/ts/src/royalur/model/Board.js";
import type { Engine } from "@rosette/ts/src/royalur/engine/Engine.js";
import type { GameState } from "@rosette/ts/src/royalur/engine/state/GameState.js";


interface BoardRenderState {
    ctx: CanvasRenderingContext2D;
    width: number;
    height: number;
    rules: Engine;
    state: GameState;
    board: Board;
    light: PlayerState;
    dark: PlayerState;
    boardShape: BoardShape;
    paths: PathPair;
    leftPlayer: PlayerType;
    ignoreDrawTiles: Tile[];
}


class BoardRenderer extends CanvasRenderer {
    public static readonly PIECE_FADE_DURATION: Seconds = 0.15;

    /**
     * Moves take longer if they move over more tiles.
     */
    static readonly MOVE_DURATIONS: Seconds[] = [0, 0.35, 0.45, 0.55, 0.65];
    static readonly SCORE_DURATION: Seconds = 0.15;
    static readonly CAPTURE_DURATION: Seconds = 0.1;
    static readonly MOVE_ROSETTE_FLASH_DURATION: Seconds = 0.3;
    static readonly DRAG_DRAW_LINE_THRESHOLD = 0.25;
    static readonly PIECE_HOVERED_WIDTH = 1.1;
    static readonly HORIZONTAL_STACK_MOVEMENT_DIR = new Vec2(8, 0);
    static readonly VERTICAL_STACK_MOVEMENT_DIR = new Vec2(0, 8);

    static readonly AUDIO_DELAY = -0.1;

    private readonly controller: BoardController;
    private mouseListener: MouseListener;
    private readonly sharedRendererState: SharedRendererState;
    private readonly assetLoader: AssetLoader;

    private readonly theme: GameTheme;
    private readonly boardAsset: BoardAsset;
    private readonly horizontal: boolean;
    private readonly drawStartEndPieces: boolean;
    private readonly skipKeyListeners: boolean;

    private readonly stackMovementDir: Vec2;

    // Linked dynamically to avoid re-creating the renderer when they change.
    private players: GamePlayers | null = null;
    private preferences: APIGamePreferences | null = null;

    private isPathActive: boolean = false;
    private readonly pathAlphaFade = new Fade(0.2);

    private readonly lightStartFade = new Fade(BoardRenderer.PIECE_FADE_DURATION);
    private readonly darkStartFade = new Fade(BoardRenderer.PIECE_FADE_DURATION);

    private readonly curveCache = new LRUCache<Vec2[]>("curveCache", 3);
    private readonly renderCurveCache = new LRUCache<Vec2[]>("renderCurveCache", 3);

    private lastDirective: BoardDirective | null = null;
    private selectedTile: Tile | null = null;

    private endDirectiveNextFrame: boolean = false;
    private replayMovesIndex: number = 0;
    private lastReplayMovesIndex: number = -1;

    private moveStartTime: Seconds = LONG_TIME_AGO;
    private moveDuration: Seconds = 0;
    private animDuration: Seconds = 0;
    private moveGrantsExtraRoll: boolean = false;
    private moveAndAnimDuration: Seconds = 0;
    private playedPlaceSound: boolean = false;
    private playedVictorySound: boolean = false;

    constructor(
        controller: BoardController,
        mouseListener: MouseListener,
        sharedRendererState: SharedRendererState,
        assetLoader: AssetLoader,
        theme: GameTheme,
        horizontal: boolean,
        drawStartEndPieces: boolean,
        skipKeyListeners: boolean,
    ) {
        super();
        this.controller = controller;
        this.sharedRendererState = sharedRendererState;
        this.assetLoader = assetLoader;
        this.mouseListener = mouseListener;
        this.theme = theme;
        this.boardAsset = this.theme.createBoardAsset();
        this.horizontal = horizontal;
        this.drawStartEndPieces = drawStartEndPieces;
        this.skipKeyListeners = skipKeyListeners;
        this.stackMovementDir = (
            horizontal
                ? BoardRenderer.HORIZONTAL_STACK_MOVEMENT_DIR
                : BoardRenderer.VERTICAL_STACK_MOVEMENT_DIR
        );
    }

    setPlayers(players: GamePlayers) {
        this.players = players;
    }

    getPlayers(): GamePlayers {
        if (this.players === null)
            throw new Error("players is not yet available");
        return this.players;
    }

    setPreferences(preferences: APIGamePreferences) {
        this.preferences = preferences;
    }

    getPreferences(): APIGamePreferences {
        if (this.preferences === null)
            throw new Error("preferences is not yet available");
        return this.preferences;
    }

    override attachListeners(): () => void {
        const mouseDownCallback = (state: MouseState) => this.onMouseDown(state);
        const mouseMoveCallback = (state: MouseState) => this.onMouseMove(state);
        const mouseReleaseCallback = (state: MouseState) => this.onMouseRelease(state);
        const keyPressCallback = (event: KeyboardEvent) => this.onKeyPress(event);

        this.mouseListener.addMouseDownListener(mouseDownCallback);
        this.mouseListener.addMouseMoveListener(mouseMoveCallback);
        this.mouseListener.addMouseReleaseListener(mouseReleaseCallback);

        let unregisterKeyListener: (() => void) | null = null;
        if (!this.skipKeyListeners) {
            unregisterKeyListener = registerKeyListener(keyPressCallback);
        }

        return () => {
            this.mouseListener.removeMouseDownListener(mouseDownCallback);
            this.mouseListener.removeMouseMoveListener(mouseMoveCallback);
            this.mouseListener.removeMouseReleaseListener(mouseReleaseCallback);
            if (unregisterKeyListener !== null) {
                unregisterKeyListener();
            }
        };
    }

    getBoardAsset(): BoardAsset {
        return this.boardAsset;
    }

    setPathActive(isPathActive: boolean) {
        if (isPathActive && !this.isPathActive) {
            this.unselectTile();
        }
        this.isPathActive = isPathActive;
        this.pathAlphaFade.fadeInOrOut(isPathActive);
    }

    selectTile(tile: Tile, skipPickupSound: boolean = false) {
        this.selectedTile = tile;
        if (!skipPickupSound) {
            this.playPickupSound();
        }
    }

    unselectTile() {
        this.selectedTile = null;
    }

    private processDirectives(): {
        rawDirective: BoardDirective;
        directive: BoardDirective;
        moveDirective: MoveBoardDirective | null;
        playDirective: PlayBoardDirective | null;
        analysisDirective: AnalysisBoardDirective | null;
    } {
        // 1) Check and handle any new directive.
        const directive = this.controller.getActiveDirective();
        if (directive.getDirectiveID() !== this.lastDirective?.getDirectiveID()) {
            this.lastDirective = directive;
            this.onNewDirective(directive);

        }

        // 2) Get the directive we want to render this frame.
        const directives = this.getDirective();

        // 3) Check and handle any new replay moves sub-directive.
        if (
            directive instanceof ReplayMovesBoardDirective
            && this.replayMovesIndex !== this.lastReplayMovesIndex
        ) {
            this.lastReplayMovesIndex = this.replayMovesIndex;
            this.startQuickMoveDirective(directive);
        }

        // 4) Check if we want to get a new directive to render next frame.
        const time = getTimeSeconds();
        const animTime = time - this.moveStartTime;

        if (animTime >= this.moveAndAnimDuration) {
            if (directive instanceof MoveBoardDirective) {
                // Should see the new directive next frame.
                this.controller.popActiveDirective(directive);

            } else if (directive instanceof ReplayMovesBoardDirective) {
                this.replayMovesIndex += 1;
                if (this.endDirectiveNextFrame || this.replayMovesIndex >= directive.moves.length) {
                    // Should see the new directive next frame.
                    this.controller.popActiveDirective(directive);
                }
            }
        }
        this.endDirectiveNextFrame = false;

        // Update the sharedRendererState with the directive we are rendering this frame.
        this.sharedRendererState.currentMoveIndex.set(directives.directive.moveIndex);
        this.sharedRendererState.isAnimatingQuickMoves.set(directive instanceof ReplayMovesBoardDirective);
        return directives;
    }

    private getDirective(): {
        rawDirective: BoardDirective;
        directive: BoardDirective;
        moveDirective: MoveBoardDirective | null;
        playDirective: PlayBoardDirective | null;
        analysisDirective: AnalysisBoardDirective | null;
    } {
        const rawDirective = this.controller.getActiveDirective();

        let directive: BoardDirective;
        if (rawDirective instanceof ReplayMovesBoardDirective) {
            directive = rawDirective.moves[this.replayMovesIndex];
        } else {
            directive = rawDirective;
        }

        return {
            rawDirective,
            directive,
            moveDirective: (directive instanceof MoveBoardDirective ? directive : null),
            playDirective: (directive instanceof PlayBoardDirective ? directive : null),
            analysisDirective: (directive instanceof AnalysisBoardDirective ? directive : null),
        };
    }

    private onNewDirective(directive: BoardDirective) {
        this.mouseListener.clearHover();

        if (directive instanceof ReplayMovesBoardDirective) {
            this.replayMovesIndex = 0;
            this.lastReplayMovesIndex = -1;

        } else {
            this.processDirectiveStart(directive, 1.0, 0.0);
        }
    }

    private startQuickMoveDirective(directive: ReplayMovesBoardDirective) {
        if (this.replayMovesIndex >= directive.moves.length)
            throw new Error("Out of bounds");

        const moveDirective = directive.moves[this.replayMovesIndex];

        let wait = 0.0;
        let speed = 0.75;
        if (directive.moveProperties) {
            const props = directive.moveProperties[this.replayMovesIndex];
            if (props === undefined)
                throw new Error("Out of bounds");

            wait = props.wait;
            speed = props.speed;
        }
        this.processDirectiveStart(moveDirective, speed, wait);
    }

    private processDirectiveStart(directive: BoardDirective, speed: number, wait: number) {
        if (directive instanceof PlayBoardDirective || directive instanceof AnalysisBoardDirective) {
            if (directive instanceof PlayBoardDirective) {
                const availableMoves = directive.availableMoves;
                const paths = directive.rules.getPaths();

                if (availableMoves && availableMoves.length === 1
                    && this.getPreferences().autoSelectSingleMoveOption) {

                    const forcedMove = availableMoves[0];
                    this.selectTile(forcedMove.getSource(paths), true);
                } else {
                    this.unselectTile();
                }

                if (directive.isGameWon) {
                    this.playVictorySound();
                }

            } else {
                this.unselectTile();
            }

            this.moveStartTime = LONG_TIME_AGO;
            this.moveDuration = LONG_TIME_AGO;
            this.moveGrantsExtraRoll = false;
            this.moveAndAnimDuration = LONG_TIME_AGO;

        } else if (directive instanceof MoveBoardDirective) {

            const move = directive.move;
            const paths = directive.rules.getPaths();
            this.moveStartTime = getTimeSeconds() + wait;

            const length = move.getLength(paths);
            const winningMoveSlowdown = (directive.moveWinsGame ? 1.5 : 1);
            this.moveDuration = (
                speed * BoardRenderer.MOVE_DURATIONS[length] * winningMoveSlowdown
            );

            if (move.isScore()) {
                this.animDuration = speed * BoardRenderer.SCORE_DURATION * winningMoveSlowdown;
            } else if (move.isCapture()) {
                this.animDuration = speed * BoardRenderer.CAPTURE_DURATION;
            } else {
                this.animDuration = 0;
            }

            const boardShape = directive.rules.getBoardShape();

            let moveGrantsExtraRoll = move.isLandingOnRosette(boardShape);
            if (directive.rules.doCapturesGrantExtraRolls() && move.isCapture()) {
                moveGrantsExtraRoll = true;
            }
            this.moveGrantsExtraRoll = moveGrantsExtraRoll;

            let moveAnimDuration = this.moveDuration + this.animDuration;
            if (this.moveGrantsExtraRoll) {
                moveAnimDuration += speed * BoardRenderer.MOVE_ROSETTE_FLASH_DURATION;
            }
            this.moveAndAnimDuration = moveAnimDuration;
            this.playedPlaceSound = false;
            this.playedVictorySound = false;
        } else {
            throw new Error(`Unexpected board directive type: ${directive.constructor.name}`);
        }
    }

    popDirectiveNextFrame() {
        this.moveStartTime = getTimeSeconds() - this.moveAndAnimDuration - 0.1;
        this.endDirectiveNextFrame = true;
    }

    onMouseDown(state: MouseState): boolean {
        if (state.location === null)
            throw new Error("onMouseDown with no mouse location");
        if (!this.boardAsset.hasBeenRendered())
            return false;

        const { playDirective } = this.getDirective();
        if (playDirective === null)
            return false;

        // Check whether the user clicked on a tile.
        const tile = this.boardAsset.getTileAt(state.location, this.getPlayers().getLeftPlayer());
        if (tile === null) {
            this.unselectTile();
            return false;
        }

        // Check if there is a possible move from the clicked piece.
        if (playDirective.availableMoves !== null) {
            const paths = playDirective.rules.getPaths();
            const moveBySource = Move.findMoveBySourceTile(
                playDirective.availableMoves, tile, paths,
            );
            if (moveBySource !== null) {
                if (!state.isTouch || this.selectedTile?.equals(tile)) {
                    this.controller.handleMove(moveBySource, false);
                } else {
                    this.selectTile(tile);
                }
                return true;
            }

            if (this.selectedTile !== null) {
                const selectedMove = Move.findMoveBySourceTile(
                    playDirective.availableMoves, this.selectedTile, paths,
                );
                if (selectedMove !== null && tile.equals(selectedMove.getDest(paths))) {
                    this.controller.handleMove(selectedMove, false);
                }
            }
        }

        // Clicked on a tile/piece not associated with an available move.
        this.unselectTile();
        return false;
    }

    onMouseMove(state: MouseState): boolean {
        if (state.location === null)
            return false;

        return state.mouseDown && this.selectedTile !== null;
    }

    onMouseRelease(state: MouseState): boolean {
        if (this.selectedTile === null || state.location === null)
            return false;
        if (!this.boardAsset.hasBeenRendered())
            return false;

        const { playDirective } = this.getDirective();
        if (playDirective === null)
            return false;

        const availableMoves = playDirective.availableMoves;
        if (availableMoves === null)
            return false;

        const paths = playDirective.rules.getPaths();
        const move = Move.findMoveBySourceTile(availableMoves, this.selectedTile, paths);
        if (move === null)
            return false;

        const destTile = move.getDest(paths);
        const hoveredTile = this.boardAsset.getTileAt(state.location, this.getPlayers().getLeftPlayer());
        if (!hoveredTile?.equals(destTile))
            return false;

        this.playPlaceSound();
        this.controller.handleMove(move, true);
        return true;
    }

    onKeyPress(event: KeyboardEvent) {
        const { playDirective, directive, rawDirective } = this.getDirective();

        const keyIsLeft = Key.ARROW_LEFT.isKey(event.key);
        const keyIsUp = Key.ARROW_UP.isKey(event.key);
        const keyIsRight = Key.ARROW_RIGHT.isKey(event.key);
        const keyIsDown = Key.ARROW_DOWN.isKey(event.key);
        if (keyIsLeft || keyIsUp || keyIsRight || keyIsDown) {
            event.stopPropagation();
            event.preventDefault();

            // Allow speedy navigation in analysis.
            if (rawDirective instanceof ReplayMovesBoardDirective) {
                this.popDirectiveNextFrame();
            }

            const type = (
                keyIsRight || keyIsDown
                    ? NavigationType.NEXT
                    : NavigationType.PREV
            );
            this.controller.handleNavigation(new NavigationGameEvent(
                type, directive.moveIndex,
            ));
            return;
        }

        const keyIsEnter = Key.ENTER.isKey(event.key) || Key.Q.isKey(event.key);
        const keyIsSpace = Key.SPACE.isKey(event.key);
        if (!keyIsEnter && !keyIsSpace)
            return;

        const availableMoves = playDirective?.availableMoves;
        if (!playDirective || !availableMoves) {
            const type = (
                directive instanceof MoveBoardDirective
                    ? NavigationType.PAUSE
                    : NavigationType.PLAY
            );
            const captureEvent = this.controller.handleNavigation(new NavigationGameEvent(
                type, directive.moveIndex,
            ));
            if (captureEvent) {
                event.stopPropagation();
                event.preventDefault();
            }
            return;
        }

        event.stopPropagation();
        event.preventDefault();
        const paths = playDirective.rules.getPaths();

        // Move the selected piece.
        if (this.selectedTile !== null && (keyIsEnter || availableMoves.length === 1)) {
            const move = Move.findMoveBySourceTile(
                availableMoves, this.selectedTile, paths,
            );
            if (move !== null) {
                this.controller.handleMove(move, false);
                return;
            }
        }

        // Select the available piece.
        if (availableMoves.length === 1 && this.selectedTile === null) {
            const move = availableMoves[0];
            this.selectTile(move.getSource(paths));
            return;
        }

        // Cycle through available moves to select.
        if (keyIsSpace && availableMoves.length > 1) {
            const sortedMoves = [...availableMoves];
            sortedMoves.sort(function (move1, move2) {
                if (move1.isScore() && move2.isScore())
                    return 0;
                if (move1.isScore())
                    return 1;
                if (move2.isScore())
                    return -1;
                return move1.getDestPiece().getPathIndex() - move2.getDestPiece().getPathIndex();
            });

            let selectedIndex = -1;
            for (let index = 0; index < sortedMoves.length; ++index) {
                const move = sortedMoves[index];
                if (move.getSource(paths).equals(this.selectedTile)) {
                    selectedIndex = index;
                    break;
                }
            }

            const targetIndex = (selectedIndex + 1) % sortedMoves.length;
            this.selectTile(sortedMoves[targetIndex].getSource(paths));
        }
    }

    playPickupSound() {
        this.theme.getPickupSound().play();
    }

    playPlaceSound() {
        this.theme.getPlaceSound().play();
    }

    playRosetteSound() {
        this.theme.getRosetteSound().play();
    }

    playCaptureSound() {
        this.theme.getCaptureSound().play();
    }

    playScoreSound() {
        this.theme.getScoreSound().play();
    }

    playVictorySound() {
        if (this.playedVictorySound)
            return;

        this.playedVictorySound = true;
        return this.theme.getVictorySound().play(true);
    }

    maybeStartMusic() {
        if (!this.controller.isMusicEnabled())
            return;

        const activeTrack = this.assetLoader.getActiveMusicTrack().get().getOrNull();
        if (activeTrack)
            return;

        this.theme.findMusicTrackToPlay()?.tryPlay();
    }

    getDrawPieceCountAndOwner(
        renderState: BoardRenderState,
        tile: Tile,
        forActiveMove?: boolean,
    ): [number, PlayerType | undefined] {
        if (!this.drawStartEndPieces || renderState.board.contains(tile)) {
            const owner = renderState.board.get(tile)?.getOwner();
            return [owner ? 1 : 0, owner];
        }

        const paths = renderState.paths;
        const isLightStartPiece = Tile.areEqual(tile, paths.getStart(PlayerType.LIGHT));
        const isDarkStartPiece = Tile.areEqual(tile, paths.getStart(PlayerType.DARK));
        const isLightEndPiece = Tile.areEqual(tile, paths.getEnd(PlayerType.LIGHT));
        const isDarkEndPiece = Tile.areEqual(tile, paths.getEnd(PlayerType.DARK));

        let pieceOwner: PlayerType | undefined = undefined;
        let pieceCount = 0;
        if (isLightStartPiece || isDarkStartPiece) {
            pieceOwner = (isLightStartPiece ? PlayerType.LIGHT : PlayerType.DARK);
            pieceCount = (
                isLightStartPiece
                    ? renderState.light.getPieceCount()
                    : renderState.dark.getPieceCount()
            );
            if (!forActiveMove && Tile.listContains(renderState.ignoreDrawTiles, tile)) {
                pieceCount = Math.max(0, pieceCount - 1);
            }
        } else if (isLightEndPiece || isDarkEndPiece) {
            pieceOwner = (isLightEndPiece ? PlayerType.LIGHT : PlayerType.DARK);
            pieceCount = (
                isLightEndPiece
                    ? renderState.light.getScore()
                    : renderState.dark.getScore()
            );
            if (forActiveMove) {
                pieceCount += 1;
            }
        }
        return [pieceCount, pieceCount > 0 ? pieceOwner : undefined];
    }

    getDrawTileBounds(
        renderState: BoardRenderState,
        tile: Tile,
        forActiveMove?: boolean,
        pieceCount?: number,
        pieceOwner?: PlayerType,
    ): Rectangle {
        const bounds = this.boardAsset.getTileBounds(tile, renderState.leftPlayer);
        if (!this.drawStartEndPieces || renderState.boardShape.contains(tile))
            return bounds;

        if (pieceCount === undefined) {
            const [drawPieceCount, drawPieceOwner] = this.getDrawPieceCountAndOwner(
                renderState, tile, forActiveMove,
            );
            pieceCount = drawPieceCount;
            pieceOwner = drawPieceOwner;
        }

        // Move to where the top piece on the stack should be rendered.
        let output = bounds.add(this.stackMovementDir.mul(-pieceCount));

        // Hack to account for oddity in the board layouts (the board's border makes it weird).
        const baselineMovement = (pieceOwner === PlayerType.LIGHT ? -0.5 : (this.horizontal ? 1.5 : 0.5));
        output = output.add(
            BoardRenderer.HORIZONTAL_STACK_MOVEMENT_DIR.mul(baselineMovement),
        );
        return output;
    }

    override render(
        ctx: CanvasRenderingContext2D,
        width: number,
        height: number,
    ): void {
        const {
            directive, moveDirective,
            playDirective, analysisDirective,
        } = this.processDirectives();

        const rules = directive.rules;
        const state = directive.getState();
        const board = state.getBoard();
        const boardShape = board.getShape();
        const light = state.getLightPlayer();
        const dark = state.getDarkPlayer();
        const move = moveDirective?.move ?? null;
        const activePlayer = directive.getActivePlayer();
        const paths = rules.getPaths();
        const leftPlayer = this.getPlayers().getLeftPlayer();

        const ignoreDrawTiles: Tile[] = [];
        const renderState: BoardRenderState = {
            ctx, width, height,
            rules, state, board, light, dark,
            boardShape, paths, leftPlayer,
            ignoreDrawTiles,
        };

        // Clear the board.
        this.clearCanvas(ctx, width, height);
        this.boardAsset.draw(ctx, 0, 0, width, height);

        // Determine whether the player can introduce a piece to the board.
        const availableMoves = playDirective?.availableMoves ?? [];
        const introductionMove = Move.findIntroductionMove(availableMoves);
        const hasIntroducingMove = (introductionMove !== null);
        const introductionTile = paths.getStart(activePlayer);

        // Determine the piece that is being hovered.
        const mouseState = this.mouseListener.getMouseState();
        let hoveredTile: Tile | null = null;
        let hoveredPiece: Piece | null = null;
        this.sharedRendererState.highlightIntroducedPiece = (
            !move && introductionTile.equals(this.selectedTile) ? activePlayer : null
        );
        if (mouseState.location !== null) {
            hoveredTile = this.boardAsset.getTileAt(mouseState.location, this.getPlayers().getLeftPlayer());
            if (hoveredTile !== null) {
                // Introduction pieces don't exist on the board.
                if (introductionTile.equals(hoveredTile) && hasIntroducingMove) {
                    this.sharedRendererState.highlightIntroducedPiece = (!move ? activePlayer : null);
                    hoveredPiece = new Piece(activePlayer, -1);
                } else {
                    hoveredPiece = board.get(hoveredTile);
                }
            }
        }

        // Find the move to preview.
        let previewMove: Move | null = null;
        let previewIsValidMove: boolean = false;
        let previewReactsToMouse: boolean = true;
        let isLightStartPieceHighlighted: boolean = false;
        let isDarkStartPieceHighlighted: boolean = false;

        if (analysisDirective?.previewMove) {
            previewMove = analysisDirective.previewMove;
            previewIsValidMove = true;
            hoveredTile = null;
            hoveredPiece = null;
            previewReactsToMouse = false;

            for (const moveHighlight of analysisDirective.moveHighlights) {
                if (moveHighlight.move.isIntroduction()) {
                    if (moveHighlight.move.getPlayer() === PlayerType.LIGHT) {
                        isLightStartPieceHighlighted = true;
                    } else {
                        isDarkStartPieceHighlighted = true;
                    }
                }
            }

        } else if (availableMoves.length > 0) {
            let previewMoveSourceTile: Tile | null = null;
            let previewMoveSourcePiece: Piece | null = null;

            // Determine the source tile for the preview move.
            if (this.selectedTile !== null) {
                previewMoveSourceTile = this.selectedTile;

                // Introduction pieces don't exist on the board.
                if (hasIntroducingMove && introductionTile.equals(this.selectedTile)) {
                    previewMoveSourcePiece = new Piece(activePlayer, -1);
                } else {
                    previewMoveSourcePiece = board.get(this.selectedTile);
                }
            } else if (hoveredPiece?.getOwner() === activePlayer) {
                previewMoveSourceTile = hoveredTile;
                previewMoveSourcePiece = hoveredPiece;
            }

            // Determine the move from the source tile.
            if (previewMoveSourceTile !== null && previewMoveSourcePiece !== null) {
                previewMove = Move.findMoveBySourceTile(availableMoves, previewMoveSourceTile, paths);

                if (previewMove !== null) {
                    previewIsValidMove = true;
                } else {
                    if (!playDirective?.roll)
                        throw new Error("Unexpectedly missing a dice roll when there are available moves");

                    // No move available from the tile.
                    // Create an invalid move to preview instead.
                    const path = paths.getWithStartEnd(activePlayer);
                    const sourceIndex = previewMoveSourcePiece.getPathIndex() + 1;
                    const destIndex = min(path.length - 1, sourceIndex + playDirective.roll.value());
                    const destTile = path[destIndex];
                    const destPiece = new Piece(activePlayer, destIndex - 1);
                    // The clamped destination can be the off-board exit tile
                    // (e.g. Skiriuk's path exits above the board), where
                    // board.get would throw and kill the whole render pass.
                    const capturedPiece = board.contains(destTile) ? board.get(destTile) : null;
                    previewMove = new Move(
                        activePlayer, previewMoveSourceTile, previewMoveSourcePiece,
                        destTile, destPiece, capturedPiece,
                    );
                    previewIsValidMove = false;
                }
            }
        }

        // Determine the pieces to ignore drawing.
        if (move !== null) {
            ignoreDrawTiles.push(move.getSource(paths));
            ignoreDrawTiles.push(move.getDest(paths));
        }
        if (previewMove !== null) {
            ignoreDrawTiles.push(previewMove.getSource(paths));
            if (this.getPreferences().showMoveGuidelines) {
                ignoreDrawTiles.push(previewMove.getDest(paths));
            }
        }

        // Draw the pieces on the board that aren't involved in a move.
        const shape = board.getShape();
        const pieceWidth = this.boardAsset.getTileWidth();

        ctx.save();
        try {
            const lightStartTile = paths.getStart(PlayerType.LIGHT);
            const darkStartTile = paths.getStart(PlayerType.DARK);

            for (let y = 1; y <= shape.getHeight(); ++y) {
                for (let x = 1; x <= shape.getWidth(); ++x) {
                    const tile = new Tile(x, y);

                    const isLightStartPiece = Tile.areEqual(tile, lightStartTile);
                    const isDarkStartPiece = Tile.areEqual(tile, darkStartTile);
                    const isStartPiece = (isLightStartPiece || isDarkStartPiece);
                    const startPlayerType = (isLightStartPiece ? PlayerType.LIGHT : PlayerType.DARK);
                    const startFade = (
                        isLightStartPiece ? this.lightStartFade : this.darkStartFade
                    );

                    let pieceOwner: PlayerType | undefined = undefined;
                    let pieceCount = 1;
                    let pieceFadeValue = 1.0;
                    let bigStartPiece = false;
                    if (board.contains(tile)) {
                        if (!Tile.listContains(ignoreDrawTiles, tile)) {
                            pieceOwner = board.get(tile)?.getOwner();
                        }

                    } else if (this.drawStartEndPieces) {
                        const [drawPieceCount, drawPieceOwner] = this.getDrawPieceCountAndOwner(
                            renderState, tile,
                        );
                        pieceCount = drawPieceCount;
                        pieceOwner = drawPieceOwner;

                    } else if (isStartPiece) {
                        let showStartPiece = (
                            hasIntroducingMove && startPlayerType === activePlayer
                        );
                        if ((isLightStartPieceHighlighted && startPlayerType === PlayerType.LIGHT)
                            || (isDarkStartPieceHighlighted && startPlayerType === PlayerType.DARK)) {

                            bigStartPiece = true;
                            showStartPiece = true;
                        }

                        startFade.fadeInOrOut(showStartPiece);
                        pieceFadeValue = startFade.get();
                        if (showStartPiece || pieceFadeValue > 0) {
                            pieceOwner = startPlayerType;
                        }
                    }
                    if (!pieceOwner || pieceCount <= 0)
                        continue;

                    let drawPieceWidth = pieceWidth;
                    let pieceSelected = false;

                    if (this.selectedTile?.equals(tile)) {
                        pieceSelected = true;
                    } else if (hoveredTile?.equals(tile) && pieceOwner === activePlayer) {
                        drawPieceWidth *= BoardRenderer.PIECE_HOVERED_WIDTH;
                    }
                    if (!this.drawStartEndPieces && isStartPiece && !bigStartPiece) {
                        drawPieceWidth *= 0.95;
                    }

                    const pieceImage = this.theme.getPieceImage(pieceOwner, pieceSelected);
                    const bounds = this.getDrawTileBounds(
                        renderState, tile, false, pieceCount, pieceOwner,
                    );

                    try {
                        ctx.globalAlpha = pieceFadeValue;
                        const centre = bounds.getCentre();
                        for (let index = 0; index < pieceCount; ++index) {
                            drawPiece(
                                ctx, pieceImage,
                                centre.add(this.stackMovementDir.mul(pieceCount - (index + 1))),
                                drawPieceWidth,
                            );
                        }
                    } finally {
                        ctx.globalAlpha = 1.0;
                    }
                }
            }
        } finally {
            ctx.restore();
        }

        // Draw the moving piece.
        if (move !== null) {
            if (moveDirective === null)
                throw new Error("Unexpectedly missing moveDirective when move is available");

            if (move.isIntroduction()) {
                const startFade = (
                    move.getPlayer() === PlayerType.LIGHT
                        ? this.lightStartFade : this.darkStartFade
                );
                startFade.fadeOut(0);
            }
            ctx.save();
            try {
                this.drawMovingPiece(renderState, moveDirective, move, pieceWidth);
            } finally {
                ctx.restore();
            }
        }

        // Draw the potential move that could be made.
        if (previewMove !== null) {
            if (previewMove.isIntroduction()) {
                const startFade = (
                    previewMove.getPlayer() === PlayerType.LIGHT
                        ? this.lightStartFade : this.darkStartFade
                );
                startFade.fadeIn(1);
            }
            ctx.save();
            try {
                this.drawPreviewMove(
                    renderState, previewMove, previewIsValidMove,
                    previewReactsToMouse, pieceWidth,
                );
            } finally {
                ctx.restore();
            }
        }

        // If we are drawing a move, we want to
        // fade out the path around the board.
        if (this.isPathActive) {
            this.pathAlphaFade.fadeInOrOut(move === null && previewMove === null);
        }

        // Draw the path around the board.
        const pathAlpha = this.pathAlphaFade.get();
        if (pathAlpha > 0) {
            ctx.save();
            try {
                ctx.globalAlpha = pathAlpha;
                const fakeMove = new Move(activePlayer, null, null, null, null, null);
                const curve = this.computePathCurve(renderState, fakeMove);
                this.drawPath(
                    renderState, fakeMove, true, false, true,
                    curve, pieceWidth, null, null, null,
                );
            } finally {
                ctx.restore();
            }
        }
    }

    computePathCurve(renderState: BoardRenderState, move: Move) {
        const path = renderState.paths.getWithStartEnd(move.getPlayer());

        let startIndex: number;
        if (move.isIntroduction()) {
            startIndex = 0;
        } else {
            startIndex = move.getSourcePiece().getPathIndex() + 1;
        }

        let endIndex: number;
        if (move.isScore()) {
            endIndex = path.length - 1;
        } else {
            endIndex = move.getDestPiece().getPathIndex() + 1;
        }

        const { light, dark } = renderState;
        return this.curveCache.getOrCompute([
            renderState.boardShape, path, this.boardAsset.getBoardBounds(), renderState.leftPlayer,
            light.getPieceCount(), dark.getPieceCount(), light.getScore(), dark.getScore(),
            renderState.ignoreDrawTiles,
            startIndex, endIndex,
        ], () => {
            const movePath: Vec2[] = [];
            for (let index = startIndex; index <= endIndex; ++index) {
                const bounds = this.getDrawTileBounds(renderState, path[index], true);
                movePath.push(bounds.getCentre());
            }
            return Vec2.createBezierCurve(movePath, 1);
        });
    }

    drawMovingPiece(
        renderState: BoardRenderState,
        directive: MoveBoardDirective,
        move: Move,
        pieceWidth: number,
    ) {
        const { ctx } = renderState;
        const scoring = move.isScore();
        const capturing = move.isCapture();

        const time = getTimeSeconds();
        const animTime = time - this.moveStartTime;
        const moveAge = clamp(animTime / this.moveDuration, 0, 1);
        const doneMoving = (animTime >= this.moveDuration);
        const animAge = (
            this.animDuration > 0
                ? clamp((animTime - this.moveDuration) / this.animDuration, 0, 1)
                : 0
        );
        const curve = this.computePathCurve(renderState, move);

        if (animTime - BoardRenderer.AUDIO_DELAY > this.moveDuration && !this.playedPlaceSound) {
            this.playedPlaceSound = true;
            if (directive.moveWinsGame) {
                this.playVictorySound();

            } else if (capturing) {
                this.maybeStartMusic();
            }

            if (scoring) {
                this.playScoreSound();
            } else if (capturing) {
                // Wait until the animation progresses more.
                if (animAge < 0.65) {
                    this.playedPlaceSound = false;
                } else {
                    this.playCaptureSound();
                }
            } else if (move.isLandingOnRosette(new StandardBoardShape())) {
                this.playRosetteSound();
            } else {
                this.playPlaceSound();
            }
        }

        if (capturing) {
            const capturedPiece = move.getCapturedPiece();
            const capturedLocation = curve[curve.length - 1];
            const capturedPieceImage = this.theme.getPieceImage(capturedPiece.getOwner(), false);
            drawPiece(renderState.ctx, capturedPieceImage, capturedLocation, pieceWidth);
        }

        const curveIndex = clamp(
            (0.3 * easeInOutSine(moveAge) + 0.7 * easeInOutCubic(moveAge)) * curve.length,
            0, curve.length - 1,
        );

        const below = curve[Math.floor(curveIndex)];
        const above = curve[Math.ceil(curveIndex)];
        const aboveRatio = curveIndex % 1.0;
        const location = above.mul(aboveRatio).add(below.mul(1.0 - aboveRatio));

        const showScoreEffects = (scoring && !this.drawStartEndPieces);
        let drawWidth = pieceWidth;
        drawWidth *= (1 + 0.2 * Math.sin(moveAge * Math.PI));
        if (showScoreEffects) {
            drawWidth *= (1 + 0.25 * easeInOutSine(animAge));
        }
        if (capturing) {
            drawWidth *= (1 + 0.15 * moveAge);
            drawWidth /= (1 + 0.15 * animAge * animAge * animAge);
        }

        const pieceSelected = ((this.moveGrantsExtraRoll || scoring) && doneMoving);
        const pieceImage = this.theme.getPieceImage(move.getPlayer(), pieceSelected);

        if (showScoreEffects) {
            ctx.globalAlpha = easeInOutSine(1 - animAge);
        }
        drawPiece(ctx, pieceImage, location, drawWidth);
        if (showScoreEffects) {
            ctx.globalAlpha = 1.0;
        }
    }

    drawPath(
        renderState: BoardRenderState,
        move: Move,
        isValidMove: boolean,
        reactsToMouse: boolean,
        isFullPath: boolean,
        curve: Vec2[],
        pieceWidth: number,
        hoveredTile: Tile | null,
        draggedTile: Tile | null,
        dragLoc: Vec2 | null,
    ) {
        const { ctx, paths } = renderState;
        const time = getTimeSeconds();

        // Simplifying the line helps avoid paths with
        // many points, which Safari struggles with.
        curve = this.renderCurveCache.getOrCompute([curve], () => {
            return Vec2.simplifyLine(curve, 1);
        });

        ctx.save();
        ctx.beginPath();

        const startTile = move.getSource(paths);
        const endTile = move.getDest(paths);
        const dragging = Tile.areEqual(startTile, draggedTile);

        if (isFullPath) {
            ctx.setLineDash([pieceWidth / 3, pieceWidth / 3]);
        } else {
            ctx.setLineDash([pieceWidth / 6, pieceWidth / 3]);
        }

        // Make the dashes move over time
        let dashOffset = 0.75 * pieceWidth * time;
        if (reactsToMouse) {
            const mouseState = this.mouseListener.getMouseState();
            if (mouseState.mouseDown) {
                // Make the dashes move more slowly when dragging the tile
                const mouseDownTime = mouseState.mouseDownTime;
                const mouseDownDuration = (mouseDownTime !== null ? mouseDownTime - time : 0);
                dashOffset += 0.3 * pieceWidth * mouseDownDuration;
            }
        }
        ctx.lineDashOffset = dashOffset;

        const endLoc = curve[curve.length - 1];
        ctx.moveTo(endLoc.x, endLoc.y);

        // If dragging the piece, draw a straight line to it.
        let drawPath = true;
        if (dragging && dragLoc !== null) {
            const dragDistFromStart = dragLoc.sub(curve[0]).lengthSq();
            const drawLineThreshold = BoardRenderer.DRAG_DRAW_LINE_THRESHOLD * pieceWidth;
            if (dragDistFromStart > drawLineThreshold * drawLineThreshold) {
                drawPath = false;
                ctx.lineTo(dragLoc.x, dragLoc.y);
            }
        }

        // If the piece is on its original tile, draw the path it will take.
        if (drawPath) {
            for (let index = curve.length - 2; index >= 0; --index) {
                const point = curve[index];
                ctx.lineTo(point.x, point.y);
            }
        }

        if (isValidMove) {
            const isEndHovered = Tile.areEqual(hoveredTile, endTile);
            ctx.strokeStyle = (isEndHovered ? rgb(100, 255, 100) : (isFullPath ? rgb(230) : rgb(255)));
        } else {
            ctx.strokeStyle = rgb(255, 70, 70);
        }

        ctx.shadowColor = "#000000";
        ctx.shadowBlur = pieceWidth / 10 * (isFullPath ? 2 : 1);
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
        ctx.lineWidth = pieceWidth / 6;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.stroke();
        ctx.closePath();

        const drawPathEnd = (locIndex: number, type: "cross" | "arrow") => {
            const index1 = (locIndex < 0 ? curve.length + locIndex : locIndex);
            const index2 = (locIndex < 0 ? index1 - 1 : index1 + 1);
            const anchorLoc = curve[index1];
            let dir = anchorLoc.sub(curve[index2]).toUnitLength();
            if (dir.lengthSq() === 0) {
                dir = new Vec2(0, -1);
            }

            ctx.beginPath();
            ctx.setLineDash([]);

            const moveTo = (vec: Vec2) => ctx.moveTo(vec.x, vec.y);
            const lineTo = (vec: Vec2) => ctx.lineTo(vec.x, vec.y);

            if (type === "cross") {
                const crossSize = pieceWidth / 6;
                const crossVec = dir.rotate(Math.PI / 4).toLength(crossSize);
                moveTo(anchorLoc.add(crossVec));
                lineTo(anchorLoc.add(crossVec.rotate(Math.PI)));
                moveTo(anchorLoc.add(crossVec.rotate(-Math.PI / 2)));
                lineTo(anchorLoc.add(crossVec.rotate(-3 * Math.PI / 2)));
            } else {
                const arrowSize = pieceWidth / 4;
                const arrowVec = dir.rotate(Math.PI / 5).toLength(arrowSize);
                moveTo(anchorLoc.sub(arrowVec));
                lineTo(anchorLoc);
                moveTo(anchorLoc.sub(arrowVec.rotate(-2 * Math.PI / 5)));
                lineTo(anchorLoc);
            }

            ctx.stroke();
            ctx.closePath();
        };

        // Draw a cross if it's not a valid move
        ctx.shadowBlur = pieceWidth / 20 * (isFullPath ? 2 : 1);
        ctx.lineWidth = pieceWidth / 5.5;
        if (!isValidMove) {
            drawPathEnd(-1, "cross");
        } else if (isFullPath) {
            ctx.strokeStyle = rgb(110, 110, 255);
            drawPathEnd(0, "cross");
            ctx.strokeStyle = rgb(90, 240, 90);
            drawPathEnd(-1, "arrow");
        }
        ctx.restore();
    }

    drawPreviewMove(
        renderState: BoardRenderState,
        move: Move,
        isValidMove: boolean,
        reactToMouse: boolean,
        pieceWidth: number,
    ) {
        const { ctx, width, height, paths, boardShape } = renderState;
        const leftPlayer = this.getPlayers().getLeftPlayer();

        // Gather data about the mouse and dragging pieces.
        let draggedTile: Tile | null = null;
        let hoveredTile: Tile | null = null;
        let dragOffset: Vec2 | null = null;

        if (reactToMouse) {
            const mouseState = this.mouseListener.getMouseState();
            const mouseLoc: Vec2 | null = mouseState.location;
            const mouseDownLoc: Vec2 | null = (
                isValidMove && mouseState.mouseDown ? mouseState.mouseDownLocation : null
            );

            if (mouseLoc !== null) {
                hoveredTile = this.boardAsset.getTileAt(mouseLoc, leftPlayer);

                if (mouseDownLoc !== null) {
                    draggedTile = this.boardAsset.getTileAt(mouseDownLoc, leftPlayer);
                    dragOffset = mouseLoc.sub(mouseDownLoc);
                }
            }
        }

        // Convert the tile path to a canvas location curve
        const curve = this.computePathCurve(renderState, move);
        let dragLoc = (dragOffset !== null ? curve[0].add(dragOffset) : null);
        if (dragLoc !== null) {
            dragLoc = new Vec2(
                clamp(dragLoc.x, pieceWidth, width - pieceWidth),
                clamp(dragLoc.y, pieceWidth, height - pieceWidth),
            );
        }

        let drawPieceWidth = pieceWidth * BoardRenderer.PIECE_HOVERED_WIDTH;
        if (isValidMove) {
            drawPieceWidth *= 1 + 0.03 * Math.cos(5 * getTimeSeconds());
        }

        if (this.getPreferences().showMoveGuidelines) {
            // Draw the path!
            this.drawPath(
                renderState, move, isValidMove, reactToMouse,
                false, curve, pieceWidth, hoveredTile,
                draggedTile, dragLoc,
            );

            // Get the type of piece to draw at the destination.
            let endPieceOwner: PlayerType | null = null;
            if (move.isCapture()) {
                endPieceOwner = move.getCapturedPiece().getOwner();
            } else if (isValidMove) {
                endPieceOwner = move.getPlayer();
            }

            // Draw the destination piece!
            if (endPieceOwner !== null) {
                const moveDest = move.getDest(paths);
                const pieceHovered = !!(hoveredTile?.equals(moveDest));
                const pieceImage = this.theme.getPieceImage(endPieceOwner, pieceHovered);
                const bounds = this.boardAsset.getTileBounds(moveDest, leftPlayer);
                drawPiece(ctx, pieceImage, bounds.getCentre(), drawPieceWidth);
            }
        }

        // Draw the source tile.
        const moveSource = move.getSource(paths);
        if (dragLoc === null) {
            const sourceSelected = !!this.selectedTile?.equals(moveSource);
            const sourceHovered = !!hoveredTile?.equals(moveSource);

            const pieceHighlighted = (sourceSelected || sourceHovered);
            const pieceImage = this.theme.getPieceImage(move.getPlayer(), pieceHighlighted);
            const bounds = this.boardAsset.getTileBounds(moveSource, leftPlayer);
            drawPiece(ctx, pieceImage, bounds.getCentre(), drawPieceWidth);
        } else {
            const draggedOverTile = this.boardAsset.getTileAt(dragLoc, leftPlayer);
            const draggedOverBoard = (draggedOverTile !== null && boardShape.contains(draggedOverTile));

            drawPieceWidth = pieceWidth;
            if (draggedOverBoard) {
                drawPieceWidth *= BoardRenderer.PIECE_HOVERED_WIDTH;
            }

            const pieceImage = this.theme.getPieceImage(move.getPlayer(), true);
            drawPiece(ctx, pieceImage, dragLoc, drawPieceWidth);
        }
    }
}

function useBoardRenderer(
    players: GamePlayers,
    listener: MouseListener,
    isPathActive: boolean,
    sharedRendererState: SharedRendererState,
    horizontal: boolean,
    drawStartEndPieces: boolean,
    skipKeyListeners: boolean,
): BoardRenderer {
    const gameTheme = useGameTheme();
    const user = useOptionalUser();
    const assetLoader = useAssetLoader();
    const { preferences } = useGamePreferences(user);
    const boardController = useBoardController();

    const renderer = useMemo(
        () => new BoardRenderer(
            boardController, listener,
            sharedRendererState, assetLoader,
            gameTheme, horizontal,
            drawStartEndPieces,
            skipKeyListeners,
        ),
        [
            boardController, listener,
            sharedRendererState, assetLoader,
            gameTheme, horizontal,
            drawStartEndPieces,
            skipKeyListeners,
        ],
    );

    // Link these outside of the constructor to avoid
    // re-creating the renderer unnecessarily.
    renderer.setPlayers(players);
    renderer.setPreferences(preferences);
    renderer.setPathActive(isPathActive);
    return renderer;
}

interface BoardUIProps {
    isMobile: boolean;
    isPathActive: boolean;
    sharedRendererState: SharedRendererState;
    horizontal?: boolean;
    vertical?: boolean;
    drawStartEndPieces?: boolean;
    skipKeyListeners?: boolean;
}

export default function BoardUI({
    isMobile, isPathActive,
    sharedRendererState,
    horizontal, vertical,
    drawStartEndPieces,
    skipKeyListeners,
}: BoardUIProps) {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const listener = useMouseListener(canvasRef);
    const gameController = useGameController();
    const game = useRune(gameController.getGame());
    const players = useRune(gameController.getPlayers());
    const renderer = useBoardRenderer(
        players, listener,
        isPathActive, sharedRendererState,
        (horizontal || !isMobile) && !vertical,
        !!drawStartEndPieces,
        !!skipKeyListeners,
    );
    const theme = useGameTheme();

    let boardBg = null;
    const boardBgData = theme.getBehindBoardBgData();
    if (boardBgData !== null) {
        boardBg = (
            <Image
                className="absolute left-1/2 top-0 h-full -translate-x-1/2"
                src={boardBgData}
                alt="Background behind the board" />
        );
    }

    const boardDirective = useBoardDirective();
    const boardBoundsOptional = useRune(renderer.getBoardAsset().boardBounds);

    useEffect(() => {
        return gameController.registerBoardDirectiveSink();
    }, [gameController]);

    const moveHighlightElems = [];
    if (boardBoundsOptional.isPresent() && boardDirective instanceof AnalysisBoardDirective) {
        const boardBounds = boardBoundsOptional.get();
        const paths = game.getRules().getPaths();
        const ratio = window.devicePixelRatio ?? 1.0;

        const moveHighlights = boardDirective.moveHighlights;
        for (let index = 0; index < moveHighlights.length; ++index) {
            const moveHighlight = moveHighlights[index];
            const bounds = renderer.getBoardAsset().calculateTileBounds(
                boardBounds, moveHighlight.move.getSource(paths), players.getLeftPlayer(),
            );
            let left = bounds.left;
            let top = bounds.top;
            if (moveHighlight.move.isIntroduction()) {
                left -= 16 * ratio;
                top -= 16 * ratio;
            } else {
                left -= 8 * ratio;
                top -= 8 * ratio;
            }

            moveHighlightElems.push(
                <FadingDiv
                    key={`${index}_${Math.round(left)}_${Math.round(top)}_${moveHighlight.category.id}`}
                    options={{ duration: 0.2 }}
                    className={cn(
                        "absolute w-[32px] h-[32px] max-w-[2em] object-contain rounded-full p-0",
                        "shadow-[0_0_8px_rgba(0,0,0,0.25)] bg-shade *:w-full *:h-full",
                    )}
                    style={{
                        left: `${left / ratio}px`,
                        top: `${top / ratio}px`,
                    }}>

                    <MoveCategoryIcon
                        moveCategory={moveHighlight.category} />
                </FadingDiv>,
            );
        }
    }

    const containerRef = useRef<HTMLDivElement | null>(null);
    const containerBounds = useBounds(containerRef);

    return (
        <div
            ref={containerRef}
            className={cn(
                "relative",
                (vertical || horizontal) ? "grow"
                    : (isMobile ? "w-full h-full max-w-[46vh]" : "h-full min-w-[46vh]"),
            )}
            style={!horizontal && !vertical && !isMobile && containerBounds ? {
                minWidth: 0.46 * containerBounds.height,
            } : {}}>

            {boardBg}
            <RenderedCanvas
                className="absolute left-0 top-0 w-full h-full"
                canvasRef={canvasRef}
                renderer={renderer}
                updateEveryFrame={true}
                rotateLeft90={horizontal} />

            {moveHighlightElems}
        </div>
    );
}
